<template>
  <div>
    <el-card class="mb-16">
      <div style="display:flex; align-items:flex-end; gap:16px; flex-wrap:wrap;">
        <div>
          <label class="label">選擇專案</label>
          <el-select
            v-model="selectedProjectId"
            filterable
            placeholder="請選擇專案"
            style="width:260px;"
            @change="onProjectChange"
          >
            <el-option
              v-for="p in projectList"
              :key="p._id"
              :value="p._id"
              :label="p.project_id"
            />
          </el-select>
        </div>
        <el-button
          v-if="selectedProjectId"
          type="primary"
          :loading="saving"
          @click="save"
        >
          儲存
        </el-button>
        <el-switch
          v-model="showDisabled"
          active-text="顯示已停用用戶"
        />
        <span v-if="!loading && visibleRows.length" style="color:#909399;">
          共 {{ visibleRows.length }} 位用戶
        </span>
      </div>
    </el-card>

    <el-card v-if="selectedProjectId" v-loading="loading">
      <el-table
        ref="table"
        :data="visibleRows"
        border
        style="width:100%"
        size="mini"
        max-height="600"
      >
        <el-table-column label="用戶名稱" width="380" fixed>
          <template slot-scope="{ row }">
            <span>{{ row.name }}</span>
            <span style="font-size:11px; color:#909399; margin-left: 16px;">{{ row.email }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="roles" label="權限" width="90" fixed />
        <el-table-column label="專案權限" width="90" align="center">
          <template slot-scope="{ row }">
            <el-checkbox v-model="row.inProject" />
          </template>
        </el-table-column>
        <el-table-column label="警戒值告警" width="100" align="center">
          <template slot-scope="{ row }">
            <el-checkbox v-model="row.alertTarget" />
          </template>
        </el-table-column>
        <el-table-column label="行動值告警" width="100" align="center">
          <template slot-scope="{ row }">
            <el-checkbox v-model="row.actionTarget" />
          </template>
        </el-table-column>
        <el-table-column label="注意值告警" width="100" align="center">
          <template slot-scope="{ row }">
            <el-checkbox v-model="row.warningTarget" />
          </template>
        </el-table-column>
        <el-table-column label="儀器故障告警" width="110" align="center">
          <template slot-scope="{ row }">
            <el-checkbox v-model="row.downTarget" />
          </template>
        </el-table-column>
        <el-table-column label="DAT 故障告警" width="110" align="center">
          <template slot-scope="{ row }">
            <el-checkbox v-model="row.datDownTarget" />
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
const ROLE_ORDER = { admin: 0, thsr: 1, occ: 2, decl: 3, client: 4 };

function roleOrder(role) {
  return ROLE_ORDER[role] ?? 5;
}

function toIdSet(arr) {
  return new Set((arr || []).map(id => (typeof id === 'object' ? id._id : id)));
}

export default {
  name: 'UserProjectAlertSetting',
  data() {
    return {
      selectedProjectId: '',
      selectedProjectData: null,
      allClients: [],
      rows: [],
      loading: false,
      saving: false,
      showDisabled: false,
    };
  },
  computed: {
    projectList() {
      return this.$store.getters['project/projectList'] || [];
    },
    // 預設隱藏已停用用戶，但仍保留在 rows 中以免 save() 誤刪其既有告警設定
    visibleRows() {
      return this.showDisabled ? this.rows : this.rows.filter(r => r.roles !== 'disabled');
    },
  },
  watch: {
    showDisabled() {
      this.refreshTableLayout();
    },
  },
  mounted() {
    this.fetchAllClients();
  },
  methods: {
    async fetchAllClients() {
      this.loading = true;
      try {
        const res = await this.$axios({ category: 'admin', method: 'get', apiName: 'client' });
        this.allClients = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.$message.error('載入用戶列表失敗');
      } finally {
        this.loading = false;
      }
    },
    onProjectChange() {
      const project = this.projectList.find(p => p._id === this.selectedProjectId);
      if (!project) return;
      this.selectedProjectData = project;
      this.buildRows(project);
      this.refreshTableLayout();
    },
    // fixed 欄位在資料非同步載入、表格剛顯示時常會有表頭/內容錯位的問題（Element UI 舊版已知問題），
    // 官方建議的解法是在版面穩定後手動呼叫 doLayout() 強制重新計算固定欄位的位置與高度
    refreshTableLayout() {
      this.$nextTick(() => {
        if (this.$refs.table && typeof this.$refs.table.doLayout === 'function') {
          this.$refs.table.doLayout();
        }
      });
    },
    buildRows(project) {
      const inProjectSet = toIdSet(project.client_id);
      const alertSet    = toIdSet(project.alert_target);
      const actionSet   = toIdSet(project.action_target);
      const warningSet  = toIdSet(project.warning_target);
      const downSet     = toIdSet(project.down_target);
      const datDownSet  = toIdSet(project.document_message?.down_target);

      const rows = this.allClients.map(u => ({
        _id: u._id,
        name: u.name || '（無名稱）',
        email: u.email || '',
        roles: u.roles || '',
        inProject:    inProjectSet.has(u._id),
        alertTarget:  alertSet.has(u._id),
        actionTarget: actionSet.has(u._id),
        warningTarget: warningSet.has(u._id),
        downTarget:   downSet.has(u._id),
        datDownTarget: datDownSet.has(u._id),
      }));

      rows.sort((a, b) => {
        const projectDiff = (b.inProject ? 1 : 0) - (a.inProject ? 1 : 0);
        if (projectDiff !== 0) return projectDiff;
        return roleOrder(a.roles) - roleOrder(b.roles);
      });

      this.rows = rows;
    },
    async save() {
      if (!this.selectedProjectData) return;
      const response = await this.$confirmEdit('此操作將更新專案告警設定');
      if (response !== 'confirm') return;

      this.saving = true;
      try {
        await this.$axios({
          category: 'admin',
          method: 'put',
          apiName: `project/${this.selectedProjectId}`,
          data: {
            ...this.selectedProjectData,
            client_id:     this.rows.filter(r => r.inProject).map(r => r._id),
            alert_target:  this.rows.filter(r => r.alertTarget).map(r => r._id),
            action_target: this.rows.filter(r => r.actionTarget).map(r => r._id),
            warning_target: this.rows.filter(r => r.warningTarget).map(r => r._id),
            down_target:   this.rows.filter(r => r.downTarget).map(r => r._id),
            document_message: {
              ...(this.selectedProjectData.document_message || {}),
              down_target: this.rows.filter(r => r.datDownTarget).map(r => r._id),
            },
          },
        });

        await this.$store.dispatch('project/getProjectList');
        this.selectedProjectData = this.projectList.find(p => p._id === this.selectedProjectId);
        this.$notifySuccess('儲存成功');
      } catch (e) {
        this.$notifyError(e);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.label { display: block; margin-bottom: 6px; font-weight: bold; color: #606266; font-size: 14px; }
.mb-16 { margin-bottom: 16px; }

/*
 * fixed 欄位橫向捲動時錯位的真正原因：全域 bulma.css 的 .is-hidden { display: none !important }
 * 蓋掉了 Element UI 自己給「被固定欄位複製到主表格那份佔位 cell」用的 .is-hidden 規則
 * （Element 原意是 display: table-cell + 內容 visibility: hidden，只是視覺上隱藏、仍保留欄寬）。
 * cell 被整個 display:none 掉之後，瀏覽器的 table-layout:fixed 會把後面欄位往前遞補，
 * 造成 colgroup 欄寬跟實際儲存格對不齊，也就是使用者看到的「格子被切分/跑版」。
 * 這裡把它改回 Element 原本要的 display，讓欄位對齊恢復正常。
 */
::v-deep .el-table__cell.is-hidden {
  display: table-cell !important;
}
</style>
